/**
 * pathfinding.ts — galaxy-graph BFS helpers for AI multi-hop dispatch.
 *
 * v4M5.5 — supports the continuous Jump Area model per
 * `HYPERSPACE.md §1 + §4`. The AI dispatchers want to issue jumps
 * toward strategic destinations (colonise targets, enemy systems,
 * unscouted neighbours) that may sit beyond a single hop of the
 * fleet's hyperdrive range. This module finds the shortest
 * range-respecting path through the `state.systems` adjacency graph
 * and returns the first hop, so the AI can dispatch a stepping-stone
 * jump and re-evaluate on the next AI tick.
 *
 * Pure reader — no state mutation. Determinism: BFS frontier processed
 * in ascending systemId order; tie-breaks among equal-length paths
 * resolve to the lowest-id stepping stone.
 */

import type { GameState, SystemId } from '../state/types';

/**
 * Find the first system to jump to in order to reach `targetSystemId`
 * from `sourceSystemId`, respecting per-hop fleet range. Returns:
 *   - null if source or target don't exist in state.systems
 *   - null if source === target (no jump needed)
 *   - null if no range-respecting path exists
 *   - target itself if it's a direct in-range neighbour of source
 *   - some intermediate adjacent system otherwise (multi-hop case)
 *
 * `fleetRange` is the empire-scaled hyperdrive range — `min over fleet
 * ships of (template.hyperdriveRange × hyperdriveRangeMultiplier)`.
 * Same formula as `applyJumpShips` so the AI's pathfinder respects
 * the same gate the applier does.
 */
export function firstStepTowards(
  state: GameState,
  sourceSystemId: SystemId,
  targetSystemId: SystemId,
  fleetRange: number,
): SystemId | null {
  if (sourceSystemId === targetSystemId) return null;
  if (!Number.isFinite(fleetRange) || fleetRange <= 0) return null;
  const src = state.systems.get(sourceSystemId);
  const dst = state.systems.get(targetSystemId);
  if (!src || !dst) return null;
  const fleetRangeSq = fleetRange * fleetRange;

  // BFS with a parent map. Frontier is FIFO; each system explored once.
  // We process neighbours in ascending systemId order for determinism.
  const parent = new Map<SystemId, SystemId>();
  parent.set(sourceSystemId, sourceSystemId); // sentinel — source is its own parent
  const queue: SystemId[] = [sourceSystemId];
  let found = false;
  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (cur === targetSystemId) {
      found = true;
      break;
    }
    const sys = state.systems.get(cur);
    if (!sys) continue;
    // Sort neighbours ascending for determinism. Copy so we don't mutate.
    const neighbours = [...sys.adjacentSystemIds].sort((a, b) => a - b);
    for (const nextId of neighbours) {
      if (parent.has(nextId)) continue;
      const next = state.systems.get(nextId);
      if (!next) continue;
      // Range gate per hop, same formula as applyJumpShips.
      const dx = next.x - sys.x;
      const dy = next.y - sys.y;
      if (dx * dx + dy * dy > fleetRangeSq) continue;
      parent.set(nextId, cur);
      queue.push(nextId);
    }
  }
  if (!found) return null;
  // Walk parents back from target to source; the first step is the
  // system whose parent is source.
  let step = targetSystemId;
  let prev = parent.get(step);
  while (prev !== undefined && prev !== sourceSystemId) {
    step = prev;
    prev = parent.get(step);
  }
  if (prev === undefined) return null; // unreachable safety
  return step;
}
